import { CartLinesUpdateEvent } from '@shopify/events';

class MgmQuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange.bind(this));
    this.syncCheckedQuantity();
    this.setupBundleSubmit();
    this.syncBundleSwatches();
  }

  onChange(event) {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.hasAttribute('data-mgm-bundle-choice')) {
      this.updateBundleSelectSwatch(target);
      return;
    }

    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (target.hasAttribute('data-mgm-bundle-choice')) {
      this.syncBundleSwatches();
      return;
    }

    this.setProductQuantity(Number(target.value));
  }

  syncCheckedQuantity() {
    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (!(checkedInput instanceof HTMLInputElement)) return;

    this.setProductQuantity(Number(checkedInput.value));
  }

  setProductQuantity(quantity) {
    if (!Number.isFinite(quantity) || quantity < 1) return;

    const section = this.closest('.shopify-section') || document;
    const productForm = section.querySelector('product-form-component');
    const quantitySelector = productForm?.querySelector('quantity-selector-component');
    const quantityInput =
      quantitySelector?.querySelector('input[name="quantity"]') || productForm?.querySelector('input[name="quantity"]');

    if (!(quantityInput instanceof HTMLInputElement)) return;

    const max = Number(quantityInput.max);
    const min = Number(quantityInput.min) || 1;
    const step = Number(quantityInput.step) || 1;
    let nextQuantity = Math.max(min, quantity);

    if (Number.isFinite(max) && max > 0) {
      nextQuantity = Math.min(max, nextQuantity);
    }

    if (step > 1) {
      nextQuantity = min + Math.round((nextQuantity - min) / step) * step;
    }

    if (typeof quantitySelector?.setValue === 'function') {
      quantitySelector.setValue(String(nextQuantity));
      quantitySelector.onQuantityChange?.();
      quantitySelector.updateButtonStates?.();
      return;
    }

    quantityInput.value = String(nextQuantity);
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  setupBundleSubmit() {
    if (this.dataset.bundleSelectorsEnabled !== 'true') return;

    const section = this.closest('.shopify-section') || document;
    const productForm = section.querySelector('product-form-component');
    const form = productForm?.querySelector('form');
    if (!(productForm instanceof HTMLElement) || !(form instanceof HTMLFormElement)) return;

    form.addEventListener(
      'submit',
      (event) => {
        this.handleBundleAdd(event);
      },
      true
    );

    form.addEventListener(
      'click',
      (event) => {
        const button = event.target?.closest?.('button[name="add"], button[type="submit"]');
        if (!button || !form.contains(button)) return;

        this.handleBundleAdd(event);
      },
      true
    );
  }

  handleBundleAdd(event) {
    if (!this.shouldUseBundleSubmit()) return;

    const items = this.getBundleItems();
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!items.length) {
      window.alert('Please choose an available color for each bundle item.');
      return;
    }

    this.addBundleToCart(items);
  }

  shouldUseBundleSubmit() {
    const selectedTier = this.querySelector('.mgm-quantity-breaks__input:checked');
    if (!(selectedTier instanceof HTMLInputElement)) return false;

    const quantity = Number(selectedTier.value);
    if (!Number.isFinite(quantity) || quantity < 2) return false;

    return Boolean(
      selectedTier.closest('.mgm-quantity-breaks__option')?.querySelector('[data-mgm-bundle-selectors]')
    );
  }

  getBundleItems() {
    const selectedTier = this.querySelector('.mgm-quantity-breaks__input:checked');
    if (!(selectedTier instanceof HTMLInputElement)) return [];

    const quantity = Number(selectedTier.value);
    if (!Number.isFinite(quantity) || quantity < 2) return [];

    const selectorGroup = selectedTier.closest('.mgm-quantity-breaks__option')?.querySelector('[data-mgm-bundle-selectors]');
    if (!(selectorGroup instanceof HTMLElement)) return [];

    const variants = this.getVariants();
    const bundleOptionPosition = Number(this.dataset.bundleOptionPosition);
    if (!variants.length || !Number.isFinite(bundleOptionPosition) || bundleOptionPosition < 0) return [];

    const selectedOptions = this.getCurrentSelectedOptions();
    if (!selectedOptions.length) return [];

    const choices = Array.from(selectorGroup.querySelectorAll('[data-mgm-bundle-choice]'))
      .filter((input) => {
        if (input instanceof HTMLInputElement && input.type === 'radio') return input.checked;
        return input instanceof HTMLSelectElement;
      })
      .map((input) => input.value)
      .slice(0, quantity);

    if (choices.length !== quantity) return [];

    const items = [];
    const bundleName = this.getSelectedTierName(selectedTier);

    for (const [index, choice] of choices.entries()) {
      const options = [...selectedOptions];
      options[bundleOptionPosition] = choice;

      const variant =
        variants.find((item) => this.variantMatchesChoice(item, options, bundleOptionPosition, choice)) ||
        variants.find((item) => this.variantHasChoice(item, choice));

      if (!variant?.id || variant.available === false) return [];
      items.push({
        variantId: String(variant.id),
        quantity: 1,
        properties: {
          Bundle: bundleName,
          Item: `#${index + 1} of ${quantity}`,
          [this.dataset.bundleOptionName || 'Color']: choice,
        },
      });
    }

    return items;
  }

  async addBundleToCart(items) {
    const sections = this.getCartSectionIds();
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: Number(item.variantId),
            quantity: item.quantity,
            properties: item.properties,
          })),
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      }).then((response) => response.json());

      if (addResponse.status) {
        throw new Error(addResponse.description || addResponse.message || 'Add to cart failed');
      }

      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mgm-quantity-breaks',
          itemCount: totalQuantity,
          sections: addResponse.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferredEventPromise.reject(error);
      window.alert(error?.message || 'Add to cart failed. Please try again.');
    }
  }

  getCartSectionIds() {
    const ids = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        ids.add(component.dataset.sectionId);
      }
    }

    return Array.from(ids);
  }

  getSelectedTierName(selectedTier) {
    const option = selectedTier.closest('.mgm-quantity-breaks__option');
    const label = option?.querySelector('.mgm-quantity-breaks__label')?.textContent?.trim();
    const badge = option?.querySelector('.mgm-quantity-breaks__badge')?.textContent?.trim();

    return [label, badge].filter(Boolean).join(' - ') || `Bundle x${selectedTier.value}`;
  }

  variantMatchesChoice(variant, selectedOptions, bundleOptionPosition, choice) {
    const options = this.getVariantOptions(variant);

    if (!options.length || options[bundleOptionPosition] !== choice) return false;

    return options.every((option, index) => {
      if (index === bundleOptionPosition) return true;
      return !selectedOptions[index] || option === selectedOptions[index];
    });
  }

  variantHasChoice(variant, choice) {
    if (variant.available === false) return false;

    return this.getVariantOptions(variant).includes(choice);
  }

  getVariantOptions(variant) {
    return Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3].filter((option) => option !== undefined);
  }

  getVariants() {
    const script = this.querySelector('[data-mgm-product-variants]');
    if (!(script instanceof HTMLScriptElement)) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch {
      return [];
    }
  }

  getCurrentSelectedOptions() {
    const section = this.closest('.shopify-section') || document;
    const picker = section.querySelector('variant-picker');
    if (!picker) return [];

    const byPosition = this.getCurrentVariantOptions();
    const selectedInputs = Array.from(picker.querySelectorAll('input:checked, select'));

    for (const input of selectedInputs) {
      if (input instanceof HTMLInputElement) {
        const optionName = input.dataset.optionName;
        if (!optionName) continue;
        const fieldset = input.closest('fieldset');
        const index = Number(fieldset?.dataset.fieldsetIndex);
        if (Number.isFinite(index)) byPosition[index] = input.value;
      }

      if (input instanceof HTMLSelectElement) {
        const selected = input.options[input.selectedIndex];
        const inputId = selected?.dataset.inputId || '';
        const position = Number(inputId.split('-')[0]) - 1;
        if (Number.isFinite(position)) byPosition[position] = input.value;
      }
    }

    return byPosition;
  }

  getCurrentVariantOptions() {
    const variant = this.getCurrentVariant();

    return Array.isArray(variant?.options) ? [...variant.options] : [];
  }

  getCurrentVariant() {
    const variants = this.getVariants();
    const section = this.closest('.shopify-section') || document;
    const productForm = section.querySelector('product-form-component');
    const variantId = productForm?.querySelector('input[name="id"]')?.value;

    return variants.find((item) => String(item.id) === String(variantId));
  }

  syncBundleSwatches() {
    for (const select of this.querySelectorAll('select[data-mgm-bundle-choice]')) {
      this.updateBundleSelectSwatch(select);
    }
  }

  updateBundleSelectSwatch(select) {
    const row = select.closest('.mgm-quantity-breaks__bundle-row');
    const swatch = row?.querySelector('[data-mgm-bundle-select-swatch]');
    if (!(swatch instanceof HTMLElement)) return;

    const selectedOption = select.options[select.selectedIndex];
    const optionColor = selectedOption?.dataset.mgmBundleOptionColor;
    const color = this.getMappedColor(select.value, optionColor);

    swatch.style.setProperty('--mgm-bundle-selected-swatch', color);
    swatch.setAttribute('aria-label', select.value);
  }

  getMappedColor(value, fallbackColor = '') {
    const handle = this.handleize(value);
    const entry = this.querySelector(`[data-mgm-bundle-color-handle="${CSS.escape(handle)}"]`);

    return entry?.getAttribute('data-mgm-bundle-color') || this.getFallbackColor(handle) || fallbackColor || '#DDD4C8';
  }

  getFallbackColor(handle) {
    const colors = {
      'baikal-blue': '#7783A7',
      'baikal-matcha': '#C8C0A8',
      'baikal-dark-grey': '#555350',
      'baikal-light-grey': '#D8D1C4',
      blue: '#8FA0B5',
      'dark-grey': '#34312E',
      green: '#7E8A75',
      beige: '#C7B39B',
      'slate-grey': '#9A9A8F',
      graphite: '#111111',
      'cloud-grey': '#5F747A',
      sage: '#7E8A75',
    };

    return colors[handle] || '';
  }

  handleize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

if (!customElements.get('mgm-quantity-breaks')) {
  customElements.define('mgm-quantity-breaks', MgmQuantityBreaks);
}

